import { useEffect, useMemo, useState, useCallback, Fragment } from "react";
import { Link } from "react-router-dom";
import { Search, Loader2 } from "lucide-react";
import { motion } from "framer-motion";
import { api } from "../lib/api";
import { Input } from "../components/ui/input";
import { BookCover } from "../components/BookCover";
import FeaturedToday from "../components/FeaturedToday";
import LanguageSelect from "../components/LanguageSelect";
import SurpriseButton from "../components/SurpriseButton";
import AdBox from "../components/AdBox";
import DailyGoal from "../components/DailyGoal";

export default function Library() {
  const [books, setBooks] = useState([]);
  const [genres, setGenres] = useState([{ name: "All", count: null }]);
  const [genre, setGenre] = useState("All");
  const [lang, setLangState] = useState(() => localStorage.getItem("pba_lang") || "en");
  const setLang = (v) => { localStorage.setItem("pba_lang", v); setLangState(v); setGenre("All"); };
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [status, setStatus] = useState({ count: 0, target: 80000 });

  const LIMIT = 48;

  // debounce search
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 350);
    return () => clearTimeout(t);
  }, [q]);

  const loadGenres = useCallback(async () => {
    try {
      const r = await api.get("/books/genres", { params: { lang } });
      setGenres(r.data.genres);
    } catch {/* noop */}
  }, [lang]);

  const loadStatus = useCallback(async () => {
    try {
      const r = await api.get("/library/status");
      setStatus(r.data);
    } catch {/* noop */}
  }, []);

  const fetchBooks = useCallback(async (nextPage = 1, replace = true) => {
    if (replace) setLoading(true);
    else setLoadingMore(true);
    try {
      const params = { page: nextPage, limit: LIMIT };
      if (debouncedQ) params.q = debouncedQ;
      if (genre && genre !== "All") params.genre = genre;
      params.lang = lang;
      const r = await api.get("/books", { params });
      setTotal(r.data.total);
      setPages(r.data.pages);
      setPage(r.data.page);
      setBooks((prev) => replace ? r.data.books : [...prev, ...r.data.books]);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [debouncedQ, genre, lang]);

  // initial + when filters change
  useEffect(() => { loadGenres(); loadStatus(); }, [loadGenres, loadStatus]);
  useEffect(() => { fetchBooks(1, true); }, [fetchBooks]);

  // poll status while seeding
  useEffect(() => {
    if (status.count >= status.target) return;
    const t = setInterval(() => { loadStatus(); }, 4000);
    return () => clearInterval(t);
  }, [status.count, status.target, loadStatus]);

  const hero = useMemo(() => books[0], [books]);
  const seeding = status.count < status.target;

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 pt-14 pb-24">
      <section className="mb-14 md:mb-20 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
        <div className="lg:col-span-8">
          <div className="font-ui text-xs tracking-caps text-muted-foreground mb-6">
            <span className="text-primary">◆</span>&nbsp;&nbsp;a quiet corner of the internet
          </div>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.98] tracking-tight">
            read, or be <em className="text-primary not-italic">read to.</em>
          </h1>
          <p className="mt-6 font-reader text-lg md:text-xl leading-relaxed text-muted-foreground max-w-2xl">
            {(lang === "all" ? status.count : total).toLocaleString()}+ public-domain volumes from project gutenberg. open any spine,
            turn the pages by touch, or let the narrator take over — at whatever pace suits your afternoon.
          </p>
          {seeding && (
            <div className="mt-4 flex items-center gap-2 font-ui text-xs tracking-caps text-muted-foreground" data-testid="seed-status">
              <Loader2 className="w-3.5 h-3.5 animate-spin text-primary" />
              stocking shelves… {status.count.toLocaleString()} of {status.target.toLocaleString()}
            </div>
          )}
        </div>
        <div className="lg:col-span-4 hidden lg:flex justify-end">
          <div className="relative w-52">
            <div className="absolute -inset-2 bg-primary/10 blur-2xl rounded-full" />
            {hero && <BookCover book={hero} size="lg" />}
          </div>
        </div>
      </section>

      <div className="flex flex-col md:flex-row md:items-center gap-4 mb-8">
        <div className="relative md:w-96">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            data-testid="library-search-input"
            placeholder="search title or author…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9 rounded-full font-ui"
          />
        </div>
        <LanguageSelect value={lang} onChange={setLang} />
        <SurpriseButton lang={lang} />
      </div>

      <DailyGoal className="mb-10 max-w-xl" />

      <FeaturedToday />

      <div className="flex flex-wrap gap-2 mb-10">
        {genres.map((gn) => (
          <button
            key={gn.name}
            onClick={() => setGenre(gn.name)}
            data-testid={`filter-genre-${gn.name.toLowerCase().replace(/\s+/g, "-")}`}
            className={`font-ui text-xs tracking-caps px-3.5 py-1.5 rounded-full border transition-colors ${
              gn.name === genre
                ? "bg-primary text-primary-foreground border-primary"
                : "bg-transparent border-border hover:bg-secondary"
            }`}
          >
            {gn.name}
            {gn.count != null && <span className="ml-1.5 opacity-60">{gn.count.toLocaleString()}</span>}
          </button>
        ))}
      </div>

      <div className="rule-ornament font-ui text-xs tracking-caps mb-10">
        <span data-testid="library-total-count">
          {loading ? "loading shelves…" : `${total.toLocaleString()} volumes`}
        </span>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-x-6 gap-y-12">
        {books.map((b, i) => (
          <Fragment key={b.id}>
          {i === 12 && (
            <div className="col-span-full flex justify-center py-2">
              <AdBox />
            </div>
          )}
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: Math.min(i * 0.01, 0.3) }}
          >
            <Link to={`/book/${b.id}`} className="block group" data-testid={`book-card-${b.id}`}>
              <BookCover book={b} />
              <div className="mt-3">
                <div className="font-display text-base leading-tight group-hover:text-primary transition-colors line-clamp-2">
                  {b.title}
                </div>
                <div className="font-ui text-xs text-muted-foreground mt-1 line-clamp-1">{b.author}</div>
              </div>
            </Link>
          </motion.div>
          </Fragment>
        ))}
      </div>

      {!loading && books.length === 0 && (
        <div className="text-center py-20 font-reader text-muted-foreground italic">
          the shelves are silent — no volumes match that.
        </div>
      )}

      {page < pages && !loading && (
        <div className="text-center mt-16">
          <button
            onClick={() => fetchBooks(page + 1, false)}
            disabled={loadingMore}
            data-testid="load-more-btn"
            className="font-ui text-xs tracking-caps px-8 py-3 rounded-full border border-border hover:bg-secondary transition-colors disabled:opacity-50"
          >
            {loadingMore ? (
              <>
                <Loader2 className="inline w-3.5 h-3.5 mr-2 animate-spin" /> loading…
              </>
            ) : (
              <>load more · page {page + 1} of {pages}</>
            )}
          </button>
        </div>
      )}
    </div>
  );
}
